import React, { useMemo } from 'react';
import { Button, Skeleton, Text } from '@spark';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, LabelList } from 'recharts';
import { evalColors, LegendItem } from './summarizationUtils';

const DEFAULT_CLASSIC_METRICS = [
  { key: 'rouge_l', label: 'ROUGE-L' },
  { key: 'bleu', label: 'BLEU' },
  { key: 'meteor', label: 'METEOR' },
  { key: 'bertscore', label: 'BERTScore' },
  { key: 'compression_ratio', label: 'Compression Ratio' },
];

const isFiniteNumber = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string' && v.trim() === '') return false;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n);
};

const toNumberOrNull = (v) => {
  if (!isFiniteNumber(v)) return null;
  return typeof v === 'number' ? v : Number(v);
};

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const isClassicMetricPercentScaled = (metricKey = '', metricLabel = '') => {
  // Some backends/tools emit classic metrics as 0–100 rather than 0–1.
  // We normalize to 0–1 for consistent rendering.
  const key = String(metricKey || '').toLowerCase();
  const label = String(metricLabel || '').toLowerCase();
  return (
    key.startsWith('rouge') ||
    key === 'bleu' ||
    key === 'meteor' ||
    key === 'bertscore' ||
    label.includes('rouge') ||
    label.includes('bleu') ||
    label.includes('meteor') ||
    label.includes('bertscore')
  );
};

const normalizeClassicMetricValue = (v, metricKey, metricLabel) => {
  const n = toNumberOrNull(v);
  if (n === null) return null;

  let x = n;
  // Heuristic: if a classic metric that should be 0–1 arrives as 0–100, scale it down.
  if (isClassicMetricPercentScaled(metricKey, metricLabel) && x > 1) {
    x = x / 100;
  }
  return clamp01(x);
};

// Renders every X-axis label (recharts auto-hides overlapping ones by default,
// which made column names disappear in the narrow drawer view). Long labels are
// truncated to their column slot and the full name is shown via a native SVG
// tooltip on hover.
const ClassicAxisTick = ({ x, y, payload, width, visibleTicksCount }) => {
  const label = String(payload?.value ?? '');
  const slotWidth = width && visibleTicksCount ? width / visibleTicksCount : 90;
  const maxChars = Math.max(6, Math.floor(slotWidth / 7)); // ≈7px per character at 12px font
  const truncated = label.length > maxChars ? `${label.slice(0, maxChars - 1).trimEnd()}…` : label;
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text dy={12} textAnchor="middle" fill="#6b7280" fontSize={12}>
        <title>{label}</title>
        {truncated}
      </text>
    </g>
  );
};

const BarValueLabel = ({ x, y, width, value, dx = 0 }) => {
  if (!isFiniteNumber(value)) return null;
  const safeX = (x ?? 0) + (width ?? 0) / 2 + dx;
  const safeY = Math.max((y ?? 0) - 8, 12);
  return (
    <text x={safeX} y={safeY} textAnchor="middle" fill="#6b7280" fontSize={12} fontWeight={400}>
      {Number(value).toFixed(2)}
    </text>
  );
};

const ClassicMetricsChart = ({ data, onConfigure, showTeacher = true, loading = false, teacherLoading = false, teacherBarLoadingText }) => {
  const teacherLabel =
    data?.models?.find?.((m) => m?.key === 'teacher')?.label || data?.models?.[0]?.label || 'Teacher';
  const studentLabel =
    data?.models?.find?.((m) => m?.key === 'student')?.label || data?.models?.[1]?.label || 'Specific AI';

  const displayMetrics = useMemo(() => {
    const serverMetrics = Array.isArray(data?.metrics) ? data.metrics : null;

    // Before server data arrives, show the default columns (as placeholders).
    if (!serverMetrics) {
      return DEFAULT_CLASSIC_METRICS;
    }

    // Only show metrics that have at least one numeric value (teacher or student).
    // This prevents empty columns from appearing.
    const filtered = serverMetrics.filter((m) => {
      const tOk = isFiniteNumber(m?.teacher);
      const sOk = isFiniteNumber(m?.student);
      return tOk || sOk;
    });

    // Keep a stable, expected ordering for known metrics; append any unknowns after.
    const order = new Map(DEFAULT_CLASSIC_METRICS.map((m, idx) => [m.key, idx]));
    return [...filtered].sort((a, b) => {
      const ai = order.has(a.key) ? order.get(a.key) : 999;
      const bi = order.has(b.key) ? order.get(b.key) : 999;
      if (ai !== bi) return ai - bi;
      return String(a.label || '').localeCompare(String(b.label || ''));
    });
  }, [data?.metrics]);

  const classicTeacherHasData = useMemo(() => {
    const serverMetrics = Array.isArray(data?.metrics) ? data.metrics : null;
    if (!serverMetrics) return false;
    return serverMetrics.some((m) => isFiniteNumber(m?.teacher));
  }, [data?.metrics]);

  const effectiveTeacherLoading = teacherLoading && !classicTeacherHasData;

  const chartData = useMemo(() => {
    // If we are still in the "default columns" phase, emit null values (no bars).
    const serverMetrics = Array.isArray(data?.metrics) ? data.metrics : null;
    const isDefaultPhase = !serverMetrics;

    return (displayMetrics || []).map((m) => {
      const realTeacher = isDefaultPhase ? null : normalizeClassicMetricValue(m.teacher, m.key, m.label);
      return {
        key: m.key,
        name: m.label,
        teacher: realTeacher ?? (effectiveTeacherLoading ? 1 : null),
        student: isDefaultPhase ? null : normalizeClassicMetricValue(m.student, m.key, m.label),
      };
    });
  }, [data?.metrics, displayMetrics, effectiveTeacherLoading]);

  const teacherLoadingBarShape = (props) => {
    const { x, y, width } = props;
    const dotR = 4;
    const cx = x + width / 2;
    return (
      <g>
        <circle cx={cx} cy={y + dotR + 2} r={dotR} fill={evalColors.teacher} opacity={0.7} />
      </g>
    );
  };

  const teacherLoadingLabel = (props) => {
    const { x, y, width } = props;
    const cx = (x ?? 0) + (width ?? 0) / 2;
    return (
      <text x={cx} y={Math.max((y ?? 0) - 8, 12)} textAnchor="middle" fill="#d1d5db" fontSize={11} fontWeight={400}>
        In progress
      </text>
    );
  };

  return (
    <div
      className="fs-unmask"
      style={{ borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 20, background: '#fff' }}
    >
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <Text variant="base-semibold" style={{ fontWeight: 600, fontSize: 16, display: 'block' }}>{data?.title || 'Classic Metrics'}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 72 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
              {showTeacher && (
                <LegendItem
                  color={evalColors.teacher}
                  label={teacherLabel}
                />
              )}
              <LegendItem
                color={evalColors.student}
                label={studentLabel}
              />
            </div>
            <Button
              variant="link"
              size="small"
              onClick={loading ? undefined : onConfigure}
            >
              Configure
            </Button>
          </div>
        </div>
      </div>
      <div style={{ padding: 24 }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Skeleton shapeType="paragraph" width="100%" />
            <Skeleton shapeType="paragraph" width="80%" />
            <Skeleton shapeType="paragraph" width="60%" />
          </div>
        ) : (
          <div style={{ height: 260 }}>
            <Text variant="detail-regular" style={{ color: '#6b7280', fontSize: 12, display: 'block', marginBottom: 8 }}>
              {data?.subtitle || 'Traditional n-gram and embedding-based measurements'}
            </Text>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 28, right: 24, left: 10, bottom: 20 }}>
                <CartesianGrid stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" interval={0} tick={<ClassicAxisTick />} />
                <YAxis domain={[0, 1]} tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(v) => Number(v).toFixed(2)} />
                <RechartsTooltip
                  formatter={(v, name) => {
                    if (effectiveTeacherLoading && name === teacherLabel) return ['In progress', name];
                    return [`${Number(v).toFixed(3)}`, `${name} Score`];
                  }}
                  contentStyle={{ borderRadius: 8 }}
                />
                {showTeacher && (
                  <Bar
                    dataKey="teacher"
                    name={teacherLabel}
                    fill={effectiveTeacherLoading ? 'transparent' : evalColors.teacher}
                    radius={effectiveTeacherLoading ? undefined : [6, 6, 0, 0]}
                    barSize={14}
                    shape={effectiveTeacherLoading ? teacherLoadingBarShape : undefined}
                  >
                    <LabelList
                      dataKey="teacher"
                      position="top"
                      offset={10}
                      content={effectiveTeacherLoading ? teacherLoadingLabel : ((props) => <BarValueLabel {...props} dx={-10} />)}
                    />
                  </Bar>
                )}
                <Bar dataKey="student" name={studentLabel} fill={evalColors.student} radius={[6, 6, 0, 0]} barSize={14}>
                  <LabelList
                    dataKey="student"
                    position="top"
                    offset={10}
                    content={(props) => <BarValueLabel {...props} dx={10} />}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassicMetricsChart;

